import { useEffect, useState } from "react";
import { Menu, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import Logo from "./Logo";
import { scrollToId } from "@/lib/scroll";
import site from "@/data/site.json";

const waLink = `https://wa.me/${site.contact.whatsappNumber}?text=${encodeURIComponent(
    "Olá! Vim pelo site do Mimo & Pata (demonstração).",
)}`;

export default function Header({ menuOpen, setMenuOpen }) {
    const [scrolled, setScrolled] = useState(false);
    const { count, setOpen } = useCart();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, id) => {
        e.preventDefault();
        scrollToId(id);
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
                scrolled
                    ? "border-b border-sand/80 bg-cream/85 shadow-[0_10px_40px_rgba(23,61,53,0.07)] backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="container-x flex h-16 items-center justify-between gap-3 lg:h-20">
                <a
                    href="#inicio"
                    data-testid="navbar-logo"
                    aria-label="Ir para o início"
                    onClick={(e) => go(e, "inicio")}
                >
                    <Logo />
                </a>

                <nav className="hidden items-center gap-6 lg:flex xl:gap-8" aria-label="Navegação principal">
                    {site.nav.slice(0, 7).map((item) => (
                        <a
                            key={item.id}
                            href={`#${item.id}`}
                            data-testid={`nav-link-${item.id}`}
                            onClick={(e) => go(e, item.id)}
                            className="group relative text-[13px] font-semibold text-forest/80 transition-colors hover:text-forest"
                        >
                            {item.label}
                            <span
                                className="absolute -bottom-1 left-0 h-0.5 w-full origin-left scale-x-0 rounded-full bg-coral transition-transform duration-300 group-hover:scale-x-100"
                                aria-hidden="true"
                            />
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    <a
                        href={waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-whatsapp-button"
                        title="WhatsApp demonstrativo — não envia mensagem real"
                        className="hidden items-center gap-2 rounded-full bg-forest px-4 py-2.5 text-[13px] font-bold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-light hover:shadow-[0_12px_24px_rgba(23,61,53,0.25)] md:inline-flex"
                    >
                        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                            <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.87 9.87 0 0 0 4.79 1.22h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 18.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.87.85-.87 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.16-.48-.28z" />
                        </svg>
                        WhatsApp
                    </a>

                    <button
                        type="button"
                        data-testid="cart-toggle-button"
                        onClick={() => setOpen(true)}
                        aria-label={`Abrir cesta de demonstração (${count} itens)`}
                        className="relative grid h-11 w-11 place-items-center rounded-full border border-sand bg-cream-card text-forest transition-all duration-300 hover:-translate-y-0.5 hover:border-coral hover:shadow-[0_10px_24px_rgba(238,149,126,0.35)]"
                    >
                        <ShoppingBag className="h-[18px] w-[18px]" />
                        {count > 0 && (
                            <span
                                data-testid="cart-badge-count"
                                className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-coral px-1 text-[10px] font-extrabold text-forest"
                            >
                                {count}
                            </span>
                        )}
                    </button>

                    <button
                        type="button"
                        data-testid="menu-toggle-button"
                        onClick={() => setMenuOpen(true)}
                        aria-label="Abrir menu"
                        className="grid h-11 w-11 place-items-center rounded-full border border-sand bg-cream-card text-forest transition-colors hover:border-coral lg:hidden"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </header>
    );
}
